/**
 * 영화관 스타일 좌석 선택 컴포넌트
 * 구역 내 개별 좌석 선택 및 예약 인터페이스
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowLeftIcon,
  UserIcon,
  BanknotesIcon,
  ClockIcon,
  CheckCircleIcon,
  XMarkIcon,
  MapPinIcon,
  WifiIcon,
  ExclamationTriangleIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { useZoneWithSeats } from '../hooks/useZones';
import { Zone, SeatInZone, SEAT_STATUS_CONFIG } from '../services/zoneApi';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

interface SeatMapProps {
  zone: Zone;
  onBack: () => void;
  onSeatSelect: (seat: SeatInZone) => void;
  selectedSeat?: SeatInZone | null;
  onCheckIn?: (seat: SeatInZone) => void;
  onReserve?: (seat: SeatInZone) => void;
}

/**
 * 방향별 좌석 모양 JSX 요소 반환 함수
 */
const getSeatShape = (seat: SeatInZone, isSelected: boolean, isVip: boolean = false): JSX.Element => {
  const direction = seat.direction || 'north';
  
  // 색상 결정
  let chairColor = 'bg-amber-600'; // 의자 색상
  let deskColor = 'bg-amber-800'; // 책상 색상
  let borderColor = 'border-amber-700';
  
  if (isSelected) {
    chairColor = 'bg-blue-500';
    deskColor = 'bg-blue-700';
    borderColor = 'border-blue-600';
  } else if (!seat.is_available) {
    chairColor = 'bg-gray-400';
    deskColor = 'bg-gray-600';
    borderColor = 'border-gray-500';
  } else if (isVip) {
    chairColor = 'bg-purple-500';
    deskColor = 'bg-purple-700';
    borderColor = 'border-purple-600';
  }

  const containerStyle = `relative w-10 h-10 sm:w-12 sm:h-12 cursor-pointer transition-all duration-200 hover:scale-105 ${isSelected ? 'transform scale-105' : ''}`;
  
  // 방향에 따른 의자와 책상 배치
  switch (direction) {
    case 'north': // 북쪽을 바라봄 (책상이 위쪽)
      return (
        <div className={containerStyle}>
          {/* 책상 */}
          <div className={`absolute top-0 left-1 right-1 h-3 ${deskColor} ${borderColor} border rounded-t-sm`}></div>
          {/* 의자 */}
          <div className={`absolute bottom-1 left-2 right-2 h-6 ${chairColor} ${borderColor} border rounded-b-lg flex items-center justify-center`}>
            <span className="text-xs font-bold text-white">{seat.seat_number}</span>
          </div>
        </div>
      );
    
    case 'south': // 남쪽을 바라봄 (책상이 아래쪽)
      return (
        <div className={containerStyle}>
          {/* 의자 */}
          <div className={`absolute top-1 left-2 right-2 h-6 ${chairColor} ${borderColor} border rounded-t-lg flex items-center justify-center`}>
            <span className="text-xs font-bold text-white">{seat.seat_number}</span>
          </div>
          {/* 책상 */}
          <div className={`absolute bottom-0 left-1 right-1 h-3 ${deskColor} ${borderColor} border rounded-b-sm`}></div>
        </div>
      );
    
    case 'east': // 동쪽을 바라봄 (책상이 오른쪽)
      return (
        <div className={containerStyle}>
          {/* 의자 */}
          <div className={`absolute top-2 bottom-2 left-1 w-6 ${chairColor} ${borderColor} border rounded-l-lg flex items-center justify-center`}>
            <span className="text-xs font-bold text-white">{seat.seat_number}</span>
          </div>
          {/* 책상 */}
          <div className={`absolute top-1 bottom-1 right-0 w-3 ${deskColor} ${borderColor} border rounded-r-sm`}></div>
        </div>
      );
    
    case 'west': // 서쪽을 바라봄 (책상이 왼쪽)
      return (
        <div className={containerStyle}>
          {/* 책상 */}
          <div className={`absolute top-1 bottom-1 left-0 w-3 ${deskColor} ${borderColor} border rounded-l-sm`}></div>
          {/* 의자 */}
          <div className={`absolute top-2 bottom-2 right-1 w-6 ${chairColor} ${borderColor} border rounded-r-lg flex items-center justify-center`}>
            <span className="text-xs font-bold text-white">{seat.seat_number}</span>
          </div>
        </div>
      );
    
    default:
      return (
        <div className={containerStyle}>
          <div className={`w-full h-full ${chairColor} ${borderColor} border-2 rounded-lg flex items-center justify-center`}>
            <span className="text-xs font-bold text-white">{seat.seat_number}</span>
          </div>
        </div>
      );
  }
};


/**
 * 레이아웃 타입에 따른 좌석 배치 생성
 */
const generateSeatLayout = (seats: SeatInZone[], zone: Zone) => {
  if (zone.layout_type === 'custom') {
    // 커스텀 레이아웃: 절대 위치 기반
    return seats.map((seat) => ({
      ...seat,
      gridRow: Math.floor(seat.position_y / 50) + 1,
      gridCol: Math.floor(seat.position_x / 50) + 1,
    }));
  }

  if (zone.layout_type === 'theater') {
    // 극장식 레이아웃: 행별 배치
    const seatsByRow = seats.reduce(
      (acc, seat) => {
        const row = seat.row_label || 'A';
        if (!acc[row]) acc[row] = [];
        acc[row].push(seat);
        return acc;
      },
      {} as Record<string, SeatInZone[]>
    );

    return Object.entries(seatsByRow).map(([row, rowSeats]) => ({
      row,
      seats: rowSeats.sort(
        (a, b) => parseInt(a.seat_label || '1') - parseInt(b.seat_label || '1')
      ),
    }));
  }

  if (zone.layout_type === 'circular') {
    // 원형 레이아웃: 중심점 기준 배치
    const centerX = zone.width / 2;
    const centerY = zone.height / 2;

    return seats
      .map((seat) => {
        const angle = Math.atan2(
          seat.position_y - centerY,
          seat.position_x - centerX
        );
        const distance = Math.sqrt(
          Math.pow(seat.position_x - centerX, 2) +
            Math.pow(seat.position_y - centerY, 2)
        );
        return { ...seat, angle, distance };
      })
      .sort((a, b) => a.angle - b.angle);
  }

  // 기본 격자 레이아웃
  const seatsByRow = seats.reduce(
    (acc, seat) => {
      const row = seat.row_number || 1;
      if (!acc[row]) acc[row] = [];
      acc[row].push(seat);
      return acc;
    },
    {} as Record<number, SeatInZone[]>
  );

  return Object.entries(seatsByRow)
    .sort(([a], [b]) => parseInt(a) - parseInt(b))
    .map(([row, rowSeats]) => ({
      row: parseInt(row),
      seats: rowSeats.sort((a, b) => (a.col_number || 1) - (b.col_number || 1)),
    }));
};

const SeatMap: React.FC<SeatMapProps> = ({
  zone,
  onBack,
  onSeatSelect,
  selectedSeat,
  onCheckIn,
  onReserve,
}) => {
  const { zoneWithSeats, isLoading, error, refreshZoneWithSeats } =
    useZoneWithSeats(zone.id);
  const [hoveredSeat, setHoveredSeat] = useState<SeatInZone | null>(null);
  const [vipSeats, setVipSeats] = useState<number[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // 좌석 데이터 (웹소켓 제거됨)
  const updatedSeats = zoneWithSeats?.seats || [];

  // VIP 좌석 목록 로드
  useEffect(() => {
    const loadVipSeats = async () => {
      try {
        const response = await fetch('/api/v1/vip-check/list');
        const data = await response.json();
        if (data.seats) {
          setVipSeats(data.seats.map((s: any) => s.seat_number));
        }
      } catch (error) {
        console.error('VIP 좌석 목록 로드 실패:', error);
      }
    };
    loadVipSeats();
  }, []);

  // 수동 새로고침 함수
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshZoneWithSeats();
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const seatLayout = useMemo(() => {
    if (updatedSeats.length === 0) return [];
    return generateSeatLayout(updatedSeats, zone);
  }, [updatedSeats, zone]);

  const handleSeatClick = async (seat: SeatInZone) => {
    if (!seat.is_available) return;
    
    // VIP 좌석 체크
    const seatNum = parseInt(seat.seat_number.match(/\d+/)?.[0] || '0');
    const isVipSeat = vipSeats.includes(seatNum);
    if (isVipSeat) {
      try {
        const response = await fetch(`/api/v1/vip-check/check/${seat.seat_number}`);
        const vipData = await response.json();
        
        if (vipData.is_vip && !vipData.can_reserve) {
          alert('이 좌석은 VIP 회원 전용 좌석입니다.');
          return;
        }
      } catch (error) {
        console.error('VIP 좌석 체크 실패:', error);
      }
    }
    
    onSeatSelect(seat);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">좌석 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error || !zoneWithSeats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-gray-900">
            오류가 발생했습니다
          </h3>
          <p className="text-gray-600 mt-2">
            {error || '좌석 정보를 불러올 수 없습니다'}
          </p>
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            구역 선택으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const availableSeats = zoneWithSeats.seats.filter(
    (seat) => seat.is_available
  ).length;
  const totalSeats = zoneWithSeats.seats.length;
  const occupancyRate =
    totalSeats > 0
      ? Math.round(((totalSeats - availableSeats) / totalSeats) * 100)
      : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* 헤더 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              구역 목록으로
            </button>
            <div className="text-center">
              <h1 className="text-lg font-bold text-gray-900">{zone.name}</h1>
              <p className="text-sm text-gray-600">{zone.description}</p>
            </div>
            <div className="flex items-center justify-end">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isRefreshing
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
                title="좌석 정보 새로고침"
              >
                <ArrowPathIcon className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">새로고침</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* 구역 정보 */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {availableSeats}
              </div>
              <div className="text-sm text-gray-600">사용 가능</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {totalSeats}
              </div>
              <div className="text-sm text-gray-600">전체 좌석</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {occupancyRate}%
              </div>
              <div className="text-sm text-gray-600">사용률</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                ₩{(zone.base_hourly_rate + (zone.hourly_surcharge || 0)).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">
                시간당
                {zone.hourly_surcharge && zone.hourly_surcharge !== 0 && (
                  <div className="text-xs text-gray-500">
                    (+₩{zone.hourly_surcharge.toLocaleString()} 추가)
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 좌석 범례 */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <h3 className="text-sm font-medium text-gray-900 mb-3">좌석 상태</h3>
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center">
              <div className="w-6 h-6 bg-green-50 border-2 border-green-400 rounded mr-2"></div>
              <span className="text-gray-600">선택 가능</span>
            </div>
            <div className="flex items-center">
              <div className="w-6 h-6 bg-purple-100 border-2 border-purple-500 rounded mr-2"></div>
              <span className="text-gray-600">VIP 전용</span>
            </div>
            <div className="flex items-center">
              <div className="w-6 h-6 bg-red-100 border-2 border-gray-400 rounded mr-2 opacity-60"></div>
              <span className="text-gray-600">사용 중</span>
            </div>
            <div className="flex items-center">
              <div className="w-6 h-6 bg-yellow-100 border-2 border-gray-400 rounded mr-2 opacity-60"></div>
              <span className="text-gray-600">예약 중</span>
            </div>
            <div className="flex items-center">
              <div className="w-6 h-6 bg-blue-600 border-2 border-blue-700 rounded mr-2"></div>
              <span className="text-gray-600">선택됨</span>
            </div>
          </div>
        </div>

        {/* 좌석 맵 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              좌석 선택
            </h3>
          </div>

          {/* 격자형 또는 극장식 레이아웃 */}
          {(zone.layout_type === 'grid' || zone.layout_type === 'theater') &&
            Array.isArray(seatLayout) && (
              <div className="space-y-4">
                {seatLayout.map((rowData: any, rowIndex) => (
                  <div
                    key={rowIndex}
                    className="flex justify-center items-center gap-2"
                  >
                    {/* 행 라벨 */}
                    <div className="w-6 text-center text-sm font-medium text-gray-500">
                      {zone.layout_type === 'theater'
                        ? rowData.row
                        : `${rowData.row}`}
                    </div>

                    {/* 좌석들 */}
                    <div className="flex gap-1">
                      {rowData.seats.map(
                        (seat: SeatInZone, seatIndex: number) => (
                          <div key={seat.id} className="relative">
                            <div
                              onClick={() => handleSeatClick(seat)}
                              onMouseEnter={() => setHoveredSeat(seat)}
                              onMouseLeave={() => setHoveredSeat(null)}
                              title={`${seat.seat_number} - ${vipSeats.includes(parseInt(seat.seat_number.match(/\d+/)?.[0] || '0')) ? 'VIP 전용' : seat.is_available ? '선택 가능' : SEAT_STATUS_CONFIG[seat.status].label}`}
                              className={!seat.is_available ? 'cursor-not-allowed' : ''}
                            >
                              {getSeatShape(
                                seat,
                                selectedSeat?.id === seat.id,
                                vipSeats.includes(parseInt(seat.seat_number.match(/\d+/)?.[0] || '0'))
                              )}
                              {vipSeats.includes(parseInt(seat.seat_number.match(/\d+/)?.[0] || '0')) && (
                                <div className="absolute -top-1 -right-1">
                                  <StarIcon className="w-3 h-3 text-yellow-500" />
                                </div>
                              )}
                            </div>

                            {/* 호버 툴팁 */}
                            {hoveredSeat?.id === seat.id && (
                              <div className="absolute z-10 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap">
                                {seat.seat_number} - ₩
                                {seat.hourly_rate.toLocaleString()}/시간
                                {vipSeats.includes(parseInt(seat.seat_number.match(/\d+/)?.[0] || '0')) && ' (VIP 전용)'}
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </div>

                    {/* 행 라벨 (우측) */}
                    <div className="w-6 text-center text-sm font-medium text-gray-500">
                      {zone.layout_type === 'theater'
                        ? rowData.row
                        : `${rowData.row}`}
                    </div>
                  </div>
                ))}
              </div>
            )}

          {/* 원형 레이아웃 */}
          {zone.layout_type === 'circular' && (
            <div className="relative w-full h-96 mx-auto">
              <div className="absolute inset-0 rounded-full border-4 border-dashed border-gray-300"></div>
              {Array.isArray(seatLayout) &&
                seatLayout.map((seat: any) => {
                  const angle = seat.angle;
                  const radius = Math.min(180, seat.distance * 0.8);
                  const x = Math.cos(angle) * radius + 192; // 중심점 기준
                  const y = Math.sin(angle) * radius + 192;

                  return (
                    <div
                      key={seat.id}
                      style={{
                        position: 'absolute',
                        left: `${x}px`,
                        top: `${y}px`,
                        transform: 'translate(-50%, -50%)',
                      }}
                      onClick={() => handleSeatClick(seat)}
                      title={`${seat.seat_number} - ${vipSeats.includes(seat.seat_number) ? 'VIP 전용' : seat.is_available ? '선택 가능' : SEAT_STATUS_CONFIG[seat.status].label}`}
                      className={!seat.is_available ? 'cursor-not-allowed' : ''}
                    >
                      {getSeatShape(
                        seat,
                        selectedSeat?.id === seat.id,
                        vipSeats.includes(seat.seat_number)
                      )}
                      {vipSeats.includes(seat.seat_number) && (
                        <div className="absolute -top-1 -right-1">
                          <StarIcon className="w-3 h-3 text-yellow-500" />
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}

          {/* 커스텀 레이아웃 */}
          {zone.layout_type === 'custom' && (
            <div className="relative w-full h-96 bg-gray-50 rounded-lg overflow-hidden">
              {Array.isArray(seatLayout) &&
                seatLayout.map((seat: any) => (
                  <div
                    key={seat.id}
                    style={{
                      position: 'absolute',
                      left: `${seat.position_x}px`,
                      top: `${seat.position_y}px`,
                    }}
                    onClick={() => handleSeatClick(seat)}
                    title={`${seat.seat_number} - ${seat.is_available ? '선택 가능' : SEAT_STATUS_CONFIG[seat.status].label}`}
                    className={!seat.is_available ? 'cursor-not-allowed' : ''}
                  >
                    {getSeatShape(
                      seat,
                      selectedSeat?.id === seat.id,
                      false // 커스텀 레이아웃에서는 VIP 표시 생략
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* 선택된 좌석 정보 */}
        {selectedSeat && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-8 w-8 text-green-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    선택된 좌석: {selectedSeat.seat_number}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center text-gray-600">
                      <MapPinIcon className="w-5 h-5 mr-2" />
                      <span>{zone.name} 구역</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <BanknotesIcon className="w-5 h-5 mr-2" />
                      <span>
                        ₩{(zone.base_hourly_rate + (zone.hourly_surcharge || 0)).toLocaleString()}/시간
                      </span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <ClockIcon className="w-5 h-5 mr-2" />
                      <span>
                        ₩{(zone.base_daily_rate + (zone.daily_surcharge || 0)).toLocaleString()}/일일권
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => onCheckIn && onCheckIn(selectedSeat)}
                      className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-200"
                    >
                      이 좌석으로 입실하기
                    </button>
                    <button
                      onClick={() => onReserve && onReserve(selectedSeat)}
                      className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-200"
                    >
                      좌석 예약하기
                    </button>
                  </div>
                </div>
              </div>
              <button
                onClick={() => onSeatSelect(null as any)}
                className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
        )}

        {/* 빈 상태 */}
        {zoneWithSeats.seats.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 text-center py-12">
            <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              등록된 좌석이 없습니다
            </h3>
            <p className="text-gray-500 mb-4">
              이 구역에 아직 좌석이 설정되지 않았습니다.
            </p>
            <button
              onClick={onBack}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
            >
              다른 구역 선택하기
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeatMap;
